import {forkJoin as observableForkJoin, of as observableOf, Observable} from 'rxjs';

import {first, catchError, map} from 'rxjs/operators';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  ViewChild,
} from '@angular/core';
import {SearchScopeConfiguration} from '../../../../cache/uiconfiguration/searchscopeconfiguration';
import {SmdDataTable} from '../../../../shared/component/smd-datatable';
import {ActionBarConfiguration} from '../../../../cache/uiconfiguration/actionbarconfiguration';
import {
  SmdDataRowIcon,
  SmdDataRowMark,
  SmdDataRowModel,
} from '../../../../shared/component/smd-datatable';
import {MatDialogRef} from '@angular/material/dialog';
import {ResourceService} from '../../../resource.service';
import {LanguageService} from '../../../../language/language.service';
import {CacheService} from '../../../../cache/cache.service';
import {AddViewEvent} from '../../../events/addviewevent';
import {MultivalueDialogResult} from './multivalue-dialog-result';
import {ListviewActionButton} from '../../../../shared/component/smd-datatable';
import {FilterResult} from '../../../filterresult';
import {DialogsService} from '../../../../shared/dialog/dialogs.service';
import {EnvironmentConfigService} from '../../../../core/services/environment-config.service';

@Component({
  selector: 'multivalue-dialog',
  templateUrl: './multivalue-dialog.component.html',
  styleUrls: ['./multivalue-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class MultivalueDialogComponent {
  @ViewChild('searchList', {static: true}) searchList: SmdDataTable;
  searchListActionBar: ActionBarConfiguration[] = [ActionBarConfiguration.DefaultRecent];
  addListTitle: string = null;
  @ViewChild('valuesList', {static: true}) valuesList: SmdDataTable;

  valuesListActionBar: ActionBarConfiguration[] = [ActionBarConfiguration.DefaultFilter];
  @ViewChild('changesList', {static: true}) changesList: SmdDataTable;

  initFilterValue: string = null;

  get changesListHeaderIcon(): SmdDataRowIcon {
    if (this.singleSelection) {
      return null;
    } else {
      return new SmdDataRowIcon('delete_sweep');
    }
  }

  addAllIconTooltip: string = null;
  removeAllIconTooltip: string = null;
  changesListHeaderIconTooltip: string = null;

  _SelectedInitialValues: string[] = [];
  currentlySelectedValues: string[] = [];

  addAllowed: boolean = true;
  private _removeAllowed: boolean = true;

  get removeAllowed(): boolean {
    return this._removeAllowed;
  }

  set removeAllowed(value: boolean) {
    this._removeAllowed = value;
    if (value === true) {
      const tooltip = this.cacheService
        .getLanguageResource()
        .pipe(
          map(lr =>
            LanguageService.translate(
              'ResourceMultivalueRemoveEverything',
              'Remove every selected item.',
              lr,
            ),
          ),
        );
      this.valuesList.header.listviewActionButtons = [
        new ListviewActionButton('delete_forever', tooltip, () => {
          this.clearSelection().subscribe(() => {
            this.updateValuesListInputs();
            this.valuesList.refreshData();
            this.changesList.refreshData();
            this.searchList._updateRows();
            this.searchList.markAllRowsForCheck();
          });
        }),
      ];
    } else {
      this.valuesList.header.listviewActionButtons = null;
    }
  }

  selectionOnly: boolean = false;

  private _singleSelection: boolean = false;

  get singleSelection(): boolean {
    return this._singleSelection;
  }

  set singleSelection(singleSelection: boolean) {
    this._singleSelection = singleSelection;
    if (singleSelection) {
      if (this.currentlySelectedValues.length > 1) {
        if (this.currentlySelectedValues.length === this._SelectedInitialValues.length) {
          let equal: boolean = true;
          for (let i = 0; i < this.currentlySelectedValues.length; i++) {
            if (this._SelectedInitialValues.indexOf(this.currentlySelectedValues[i]) === -1) {
              equal = false;
            }
          }
          if (!equal) {
            this.currentlySelectedValues.length = 0;
            this.changesRowModels = this.changesRowModels.filter(
              rm => rm.mark !== SmdDataRowMark.Add,
            );
          }
        } else {
          this.currentlySelectedValues.length = 0;
          this.changesRowModels = this.changesRowModels.filter(
            rm => rm.mark !== SmdDataRowMark.Add,
          );
        }
        this.updateValuesListInputs();
      }
    }
  }

  changesRowModels: SmdDataRowModel[] = [];

  onAddView: EventEmitter<AddViewEvent> = new EventEmitter<AddViewEvent>();

  public objectDisplayName: string = null;
  public attributeLabel: Observable<string> = null;

  // Dynamic display name attribute for template
  get displayNameAttributeArray(): string[] {
    const displayNameAttr = this.envConfig.getAttributeName('DisplayName');
    return displayNameAttr ? [displayNameAttr] : [];
  }

  public set SearchScopes(sscs: SearchScopeConfiguration[]) {
    this.searchList.reloadList(sscs);
  }

  public set SelectedSearchScope(ssc: SearchScopeConfiguration) {
    this.valuesList.mergeRemoteAndLocal = true;
    this.valuesList.reloadList([ssc]);
    if (Array.isArray(ssc.attributedisplayed)) {
      this.changesList.localColumns = ssc.attributedisplayed;
    } else {
      this.changesList.localColumns = ssc.attributedisplayed.split(';');
    }
    this.changesList.localResultObjectType = ssc.resultobjecttype;
    const displayNameAttr = this.envConfig.getAttributeName('DisplayName');
    this.searchList.additionalLoadedAttributes = displayNameAttr
      ? [displayNameAttr].concat(this.changesList.localColumns)
      : this.changesList.localColumns;
    this.searchList.refreshSearchScope();
  }

  public setSelectedValues(values: string[], initialSelected?: string[]) {
    if (values == null) values = [];
    this._SelectedInitialValues = initialSelected ? initialSelected : values;
    this.currentlySelectedValues = values.slice();
  }

  public setChanges(changes: SmdDataRowModel[]) {
    this.changesRowModels = changes;
    this.updateValuesListInputs();
    this.valuesList.refreshData();
  }

  constructor(
    public dialogRef: MatDialogRef<MultivalueDialogComponent>,
    private resourceService: ResourceService,
    private cacheService: CacheService,
    private changeDetector: ChangeDetectorRef,
    private dialogService: DialogsService,
    private envConfig: EnvironmentConfigService,
  ) {
    this.cacheService
      .getLanguageResource()
      .pipe(
        catchError((error, caught) => this.handleError(error, caught)),
        first(),
      )
      .subscribe(lr => {
        this.addAllIconTooltip = LanguageService.translate(
          'ResourceMultivalueSearchlistAddAll',
          'Add all to selection',
          lr,
        );
        this.removeAllIconTooltip = LanguageService.translate(
          'ResourceMultivalueValueslistRemoveAll',
          'Remove all from selection',
          lr,
        );
        this.changesListHeaderIconTooltip = LanguageService.translate(
          'ResourceMultivalueChangeslistRemoveAll',
          'Remove changes',
          lr,
        );
        this.addListTitle = LanguageService.translate(
          'ResourceMultivalueSearchlistTitle',
          'Items To Add',
          lr,
        );
      });
  }

  readonly RemoveIcon: SmdDataRowIcon = new SmdDataRowIcon('remove_circle_outline', 'remove');
  readonly AddIcon: SmdDataRowIcon = new SmdDataRowIcon('add_circle_outline', 'add');
  readonly MoveRightIcon: SmdDataRowIcon = new SmdDataRowIcon('navigate_next');
  readonly MoveLeftIcon: SmdDataRowIcon = new SmdDataRowIcon('navigate_before');
  readonly SelectedMark: SmdDataRowMark = SmdDataRowMark.Selected;
  readonly HideMark: SmdDataRowMark = SmdDataRowMark.HideRow;
  readonly RemoveMark: SmdDataRowMark = SmdDataRowMark.Remove;

  get valuesListIcon(): SmdDataRowIcon {
    if (!this.singleSelection && this._removeAllowed) {
      return this.MoveLeftIcon;
    } else {
      return null;
    }
  }

  get valuesListHeaderIcon(): SmdDataRowIcon {
    if (this.singleSelection) {
      return null;
    } else if (this.removeAllowed) {
      return this.MoveLeftIcon;
    } else {
      return null;
    }
  }

  get valuesListHeaderIconTooltip(): string {
    if (this.removeAllowed) {
      return this.removeAllIconTooltip;
    } else {
      return null;
    }
  }

  private searchListRemoveAll: boolean = false;

  get searchListHeaderIcon(): SmdDataRowIcon {
    if (this.singleSelection) {
      return new SmdDataRowIcon(null);
    } else if (this.searchListRemoveAll) {
      return this.MoveLeftIcon;
    } else {
      return this.MoveRightIcon;
    }
  }

  get searchListHeaderIconTooltip(): string {
    if (this.searchListRemoveAll) {
      return this.removeAllIconTooltip;
    } else {
      return this.addAllIconTooltip;
    }
  }

  private _showChanges: boolean = false;

  set ShowChanges(show: boolean) {
    this._showChanges = show;
    if (show) {
      this.changesList.refreshData();
    } else {
      this.valuesList.refreshData();
    }
  }

  get ShowChanges(): boolean {
    return this._showChanges;
  }

  searchListRowClick(row: SmdDataRowModel | SmdDataRowModel[]) {
    if (!this.addAllowed) return;
    let obs: Observable<any> = observableOf(true);
    if (Array.isArray(row)) {
      if (!this.searchListRemoveAll) {
        let obsList: Observable<any>[] = [];
        row.forEach((rm: SmdDataRowModel) => {
          if (rm.mark !== SmdDataRowMark.Selected && rm.mark !== SmdDataRowMark.HideRow) {
            obsList.push(this.searchListAddClick(rm));
          }
        });

        obs = observableForkJoin(obsList);
        obs = obs.pipe(
          map(() => {
            this.searchListRemoveAll = true;
          }),
        );
      } else {
        row.forEach((rm: SmdDataRowModel) => {
          if (rm.mark !== SmdDataRowMark.Selected && rm.mark !== SmdDataRowMark.HideRow) {
            this.searchListRemoveClick(rm);
          }
        });
        this.searchListRemoveAll = false;
      }
      obs = obs.pipe(
        map(() => {
          this.searchList.markAllRowsForCheck();
        }),
      );
    } else {
      if (row.mark === SmdDataRowMark.Selected || row.mark === SmdDataRowMark.HideRow) {
        this.searchListRemoveClick(row);
      } else {
        obs = this.searchListAddClick(row);
      }
    }

    obs.subscribe(() => {
      this.updateValuesListInputs();
      this.searchList._updateRows();
      this.searchList.markAllRowsForCheck();
      this.valuesList.refreshData();
      this.changesList.refreshData();
      if (this.selectionOnly) {
        this.save();
      }
    });
  }

  searchListRemoveClick(row: SmdDataRowModel): void {
    if (!this.removeAllowed) return;
    if (row == null || row.model == null) return;
    if (row.mark !== SmdDataRowMark.Selected && row.mark !== SmdDataRowMark.HideRow) return;

    const objectIdAttr = this.envConfig.getAttributeName('ObjectID');
    if (!objectIdAttr || row.model[objectIdAttr] == null) return;
    row.mark = null;
    row.icon = this.MoveRightIcon;

    this.removeObject(new SmdDataRowModel(row.model, null, null, null));
  }

  searchListAddClick(row: SmdDataRowModel): Observable<any> {
    if (!this.addAllowed) return;
    if (row == null || row.model == null) return;
    if (row.mark !== null && row.mark !== SmdDataRowMark.Add) return;
    const objectIdAttr = this.envConfig.getAttributeName('ObjectID');
    if (!objectIdAttr || row.model[objectIdAttr] == null) return;
    // add object
    row.mark = SmdDataRowMark.HideRow;
    row.icon = this.MoveLeftIcon;

    return this.addObject(row);
  }

  valueListRowClick(row: SmdDataRowModel | SmdDataRowModel[]) {
    if (!this._removeAllowed && !this.addAllowed) return;
    if (this.singleSelection) return;
    if (row == null) return;
    let obs: Observable<any> = observableOf(true);
    if (Array.isArray(row)) {
      row.forEach((rm: SmdDataRowModel) => {
        if (rm.mark === SmdDataRowMark.Add || rm.mark == null) {
          this.removeObject(rm);
        }
      });
    } else {
      if (row.mark === SmdDataRowMark.Add || row.mark == null) {
        this.removeObject(row);
      }
    }
    obs.subscribe(() => {
      this.updateValuesListInputs();
      this.valuesList.refreshData();
      this.changesList.refreshData();
      this.searchList._updateRows();
      this.searchList.markAllRowsForCheck();
    });
  }

  changesListRowClick(row: SmdDataRowModel | SmdDataRowModel[]) {
    if (this.singleSelection) return;
    if (row == null) return;
    let obs: Observable<any> = observableOf(true);
    if (Array.isArray(row)) {
      let obsList: Observable<any>[] = [];
      row.forEach((rm: SmdDataRowModel) => {
        if (rm.mark === SmdDataRowMark.Remove) {
          obsList.push(this.addObject(rm));
        } else if (rm.mark === SmdDataRowMark.Add) {
          this.removeObject(rm);
        }
      });
      if (obsList.length !== 0) {
        obs = observableForkJoin(obsList);
      }
    } else {
      if (row.mark === SmdDataRowMark.Remove) {
        obs = this.addObject(row);
      } else if (row.mark === SmdDataRowMark.Add) {
        this.removeObject(row);
      }
    }

    obs.subscribe(() => {
      this.updateValuesListInputs();
      this.valuesList.refreshData();
      this.changesList.refreshData();
      this.searchList._updateRows();
      this.searchList.markAllRowsForCheck();
    });
  }

  addObject(row: SmdDataRowModel): Observable<void> {
    const objectIdAttr = this.envConfig.getAttributeName('ObjectID');
    if (row == null || row.model == null || !objectIdAttr || row.model[objectIdAttr] == null)
      return;
    const ObjectID: string = row.model[objectIdAttr];
    let obs: Observable<any> = observableOf(true);
    if (this._SelectedInitialValues.indexOf(ObjectID) !== -1) {
      // add initial value again
      if (this.singleSelection) {
        obs = this.clearSelection();
      }
      obs = obs.pipe(
        map(() => {
          for (let i = 0; i < this.changesRowModels.length; i++) {
            const objIdAttr = this.envConfig.getAttributeName('ObjectID');
            if (objIdAttr && this.changesRowModels[i].model[objIdAttr] === ObjectID) {
              this.changesRowModels[i].icon = this.RemoveIcon;
              this.changesRowModels[i].mark = null;
              this.changesRowModels.splice(i, 1);
              break;
            }
          }
        }),
      );
    } else {
      // add new value
      if (!this.addAllowed) return;
      if (this.singleSelection) {
        obs = this.clearSelection();
      }
      obs = obs.pipe(
        map(() => {
          this.changesRowModels.push(
            new SmdDataRowModel(row.model, false, SmdDataRowMark.Add, this.MoveLeftIcon),
          );
        }),
      );
    }

    obs = obs.pipe(
      map(() => {
        this.currentlySelectedValues.push(ObjectID);
      }),
    );
    return obs;
  }

  removeObject(row: SmdDataRowModel): void {
    const objectIdAttr = this.envConfig.getAttributeName('ObjectID');
    if (row == null || row.model == null || !objectIdAttr || row.model[objectIdAttr] == null)
      return;
    const ObjectID: string = row.model[objectIdAttr];
    if (this._SelectedInitialValues.indexOf(ObjectID) !== -1) {
      // remove initial value
      if (!this.removeAllowed) return;
      row.mark = SmdDataRowMark.Remove;
      row.icon = this.MoveRightIcon;
      this.changesRowModels.push(row);
    } else {
      // remove value added by user
      for (let i = 0; i < this.changesRowModels.length; i++) {
        const objectIdAttr = this.envConfig.getAttributeName('ObjectID');
        if (objectIdAttr && this.changesRowModels[i].model[objectIdAttr] === ObjectID) {
          this.changesRowModels.splice(i, 1);
          break;
        }
      }
    }
    this.updateValuesListInputs();
    const index = this.currentlySelectedValues.indexOf(ObjectID);
    if (index !== -1) {
      this.currentlySelectedValues.splice(index, 1);
    }
  }

  private updateValuesListInputs() {
    this.valuesList.remoteSpecialMarkedObjectIDs = this.changesRowModels
      .filter(rm => rm.mark === SmdDataRowMark.Remove)
      .map(rm => {
        const objectIdAttr = this.envConfig.getAttributeName('ObjectID');
        return objectIdAttr ? rm.model[objectIdAttr] : null;
      })
      .filter(id => id !== null);
    this.valuesList.localRowModels = this.changesRowModels.filter(
      rm => rm.mark === SmdDataRowMark.Add,
    );
  }

  onSearchListRowsReloaded(rows: SmdDataRowModel[]): void {
    for (let i = 0; i < rows.length; i++) {
      if (rows[i].mark !== SmdDataRowMark.Selected && rows[i].mark !== SmdDataRowMark.HideRow) {
        this.searchListRemoveAll = false;
        return;
      }
    }
    this.searchListRemoveAll = true;
  }

  clearSelection(): Observable<any> {
    this.currentlySelectedValues.length = 0;
    this.changesRowModels.length = 0;
    if (
      this.valuesList.selectedSearchScope == null ||
      this.valuesList.selectedSearchScope.xpath == null ||
      this.valuesList.selectedSearchScope.attributedisplayed == null
    ) {
      return observableOf(null);
    }

    let attributes: string;
    if (Array.isArray(this.valuesList.selectedSearchScope.attributedisplayed)) {
      attributes = this.valuesList.selectedSearchScope.attributedisplayed.join(',');
    } else {
      attributes = this.valuesList.selectedSearchScope.attributedisplayed.replace(/;\s*/g, ',');
    }

    return this.resourceService
      .searchResources(this.valuesList.selectedSearchScope.xpath, attributes, 99999)
      .pipe(
        catchError((error, caught) => this.handleError(error, caught)),
        first(),
        map((fr: FilterResult) => {
          if (fr == null || fr.TotalCount === 0 || fr.Results == null) return;
          this.changesRowModels.push.apply(
            this.changesRowModels,
            fr.Results.map(
              res => new SmdDataRowModel(res, null, SmdDataRowMark.Remove, this.MoveRightIcon),
            ),
          );
        }),
      );
  }

  public save(): void {
    this.dialogRef.close(
      new MultivalueDialogResult(
        this.changesRowModels,
        this.currentlySelectedValues,
        this._SelectedInitialValues,
      ),
    );
  }

  public close(): void {
    this.dialogRef.close(null);
  }

  getCaughtObservable(obs: Observable<any>): Observable<any> {
    if (obs == null) return null;
    return obs.pipe(catchError((error, caught) => this.handleError(error, caught)));
  }

  handleError(error: any, caught?: Observable<any>) {
    this.cacheService
      .getLanguageResource()
      .pipe(
        catchError((error, caught) => this.handleError(error, caught)),
        first(),
      )
      .subscribe(
        lr => {
          const title = LanguageService.translate(
            'ResourceMultivalueErrorTitle',
            'Error in Manage Dialog',
            lr,
          );
          let msg = LanguageService.translate(
            'ResourceMultivalueErrorMessage',
            'An error occurred in the manage dialog.',
            lr,
          );
          this.dialogService.webserviceError(error, title, msg, lr).subscribe(() => {
            this.dialogRef.close(null);
          });
        },
        () => {},
      );
    return observableOf(null);
  }
}
